import { FC, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Table } from '@aws-northstar/ui'
import {
	Badge,
	Box,
	Button,
	ColumnLayout,
	Container,
	ContentLayout,
	Flashbar,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'


import LoadingSpinner from '../../components/LoadingSpinner'
import RequiredPermissions from '../../helper/RequiredPermissions'
import { ACCESS_MANAGEMENT_FLASHBAR_PREFIX } from '../../helper/accessManagementFlashbar'
import { isAccessAdmin } from '../../helper/permissionRoles'

import useAllUserPermissions from '../../hooks/query/useAllUserPermissions'
import { useFlashbarStateStore } from '../../stores/notifications/flashbarStateStore'
import type { Access } from '../../types/access'

const AccessManagement: FC = () => {
	const navigate = useNavigate()
	const { items: flashbarItems } = useFlashbarStateStore()

	const { data: allUserPermissions, isLoading } = useAllUserPermissions()

	const accessManagementFlashbarItems = flashbarItems.filter((item) =>
		item.id.startsWith(ACCESS_MANAGEMENT_FLASHBAR_PREFIX)
	)

	const { totalUsers, totalMembers, totalAdmins } = useMemo(() => {
		const users = allUserPermissions || []
		const admins = users.filter((user) => isAccessAdmin(user.permissions))

		return {
			totalUsers: users.length,
			totalMembers: users.length - admins.length,
			totalAdmins: admins.length
		}
	}, [allUserPermissions])

	const tableColumnDefinitions = [
		{
			id: 'email',
			width: 280,
			header: 'User',
			cell: (data: Access) => (
				<Button
					variant="link"
					onClick={() => navigate(`/access-management/${data.userEmail}`)}
				>
					<Box variant="span" fontWeight="bold">
						<span style={{ whiteSpace: 'nowrap' }}>{data.userEmail}</span>
					</Box>
				</Button>
			)
		},
		{
			id: 'type',
			width: 100,
			header: 'Type',
			cell: (data: Access) => {
				const admin = isAccessAdmin(data.permissions)
				return (
					<Badge color={admin ? 'green' : 'grey'}>
						{admin ? 'Admin' : 'Member'}
					</Badge>
				)
			}
		},
		{
			id: 'accessSummary',
			width: 420,
			header: 'Access',
			cell: (data: Access) => (
				<SpaceBetween direction="vertical" size="xxxs">
					{(data.permissions ?? []).map((permission: string, i: number) => {
						return (
							<Badge color="blue" key={i}>
								<code>{permission}</code>
							</Badge>
						)
					})}
				</SpaceBetween>
			)
		}
	]

	const tableActions = (
		<RequiredPermissions requiredPermissions={['useraccess:*:create']}>
			<Button
				variant="primary"
				onClick={() => navigate('/access-management/new')}
			>
				New Access
			</Button>
		</RequiredPermissions>
	)

	if (!allUserPermissions || isLoading) return <LoadingSpinner />

	return (
		<>
			{accessManagementFlashbarItems.length > 0 && (
				<Flashbar items={accessManagementFlashbarItems} />
			)}
			<ContentLayout
				header={
					<Header variant="h1" actions={tableActions}>
						User Access Management
					</Header>
				}
			>
				<SpaceBetween size="l">
					<ColumnLayout columns={3} variant="default">
						<Container>
							<Box color="text-label" fontWeight="bold" fontSize="heading-xs">
								TOTAL USERS
							</Box>
							<Box variant="h1">{totalUsers}</Box>
						</Container>
						<Container>
							<Box color="text-label" fontWeight="bold" fontSize="heading-xs">
								TOTAL MEMBERS
							</Box>
							<Box variant="h1">{totalMembers}</Box>
						</Container>
						<Container>
							<Box color="text-label" fontWeight="bold" fontSize="heading-xs">
								TOTAL ADMINS
							</Box>
							<Box variant="h1">{totalAdmins}</Box>
						</Container>
					</ColumnLayout>
					<Table
						columnDefinitions={tableColumnDefinitions}
						items={allUserPermissions}
						disableRowSelect
						sortingDisabled
						loading={isLoading}
					/>
				</SpaceBetween>
			</ContentLayout>
		</>
	)
}

export default AccessManagement
